import { useAtom, useAtomValue } from 'jotai';
import { ChevronsLeft, ChevronsRight } from 'lucide-react';
import React from 'react';

import {
  cn,
  DeviceTypeDetectionVariant,
  Inline,
  Stack,
  Typography,
  useDeviceTypeDetection,
} from '@crossnokaye/ux-common';

import { SquareButton } from '@/components/navigation/left/components/shared/square-button';
import { useBranchConfig } from '@/hooks/use-branch-config';

import {
  rightPaneCollapsedContentAtom,
  rightPaneContentAtom,
  rightPaneEnabledAtom,
  rightPaneExpandedAtom,
  rightPaneTitleAtom,
} from './right-pane.state';

export const RightPane = () => {
  const [expanded, setExpanded] = useAtom(rightPaneExpandedAtom);
  const title = useAtomValue(rightPaneTitleAtom);
  const enabled = useAtomValue(rightPaneEnabledAtom);
  const content = useAtomValue(rightPaneContentAtom);
  const collapsedContent = useAtomValue(rightPaneCollapsedContentAtom);

  const toggleExpanded = () => {
    setExpanded(!expanded);
  };

  const { isMobile } = useDeviceTypeDetection({
    variant: DeviceTypeDetectionVariant.DeviceOnly,
  });

  const ResponsiveWrapper = isMobile ? MobileWrapper : DesktopWrapper;

  // Don't render anything when not visible or no content
  if (!enabled || !content) {
    return null;
  }

  return (
    <ResponsiveWrapper expanded={expanded} data-testid={'right-pane'}>
      <Stack className="h-full @container/right-pane" gap={'sm'}>
        <Inline
          className={cn('items-center justify-center gap-3 p-4', expanded && 'justify-start')}
        >
          <SquareButton
            icon={expanded ? <ChevronsRight size={'1rem'} /> : <ChevronsLeft size={'1rem'} />}
            onClick={() => toggleExpanded()}
            data-testid={'right-pane-collapse-toggle'}
          />
          {title && (
            <Typography variant="h2" className="hidden @3xs/right-pane:flex">
              {title}
            </Typography>
          )}
        </Inline>
        <div className="flex-1 overflow-hidden">{expanded ? content : collapsedContent}</div>
      </Stack>
    </ResponsiveWrapper>
  );
};

const MobileWrapper = ({
  expanded,
  children,
}: {
  expanded: boolean;
  children: React.ReactNode;
}) => {
  const { showBanner } = useBranchConfig();

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-toast bg-secondary transform transition-transform duration-300 ease-in-out',
        showBanner ? 'top-10' : 'top-0',
        {
          'translate-x-0': expanded,
          'translate-x-full': !expanded,
        }
      )}
    >
      {children}
    </div>
  );
};

// Desktop wrapper applies the width transition
const DesktopWrapper = ({
  expanded,
  children,
}: {
  expanded: boolean;
  children: React.ReactNode;
}) => {
  return (
    <div
      className={cn(
        'bg-secondary flex-shrink-0 transition-all duration-300 ease-in-out relative z-sticky',
        {
          'w-[17.5rem]': expanded,
          'w-[4.375rem]': !expanded,
        }
      )}
    >
      {children}
    </div>
  );
};
